// import { Link } from "react-router-dom";
import { useState, useRef, useEffect, useCallback } from "react";
import DotLoader from "react-spinners/DotLoader";
import axios from "axios";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

export default function LoginForm({ setVisible }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Step state: 0 = enter email, 1 = enter OTP
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [loading] = useState(false);
  const [error, setError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleScriptReady, setGoogleScriptReady] = useState(false);
  const otpRefs = useRef([]);

  // Poll for Google script (loads async in index.html) so we re-render when ready
  useEffect(() => {
    const check = () => {
      if (window.google?.accounts?.id) {
        setGoogleScriptReady(true);
        return true;
      }
      return false;
    };
    if (check()) return;
    const id = setInterval(() => check() && clearInterval(id), 200);
    const timeout = setTimeout(() => clearInterval(id), 15000);
    return () => {
      clearInterval(id);
      clearTimeout(timeout);
    };
  }, []);

  // Send OTP to email for login
  const handleSendOtp = async () => {
    setOtpError("");
    setOtpLoading(true);
    try {
      await axios.post(`/api/auth/send-login-otp`, { email });
      setOtpSent(true);
      setStep(1);
    } catch (err) {
      setOtpError(
        err.response?.data?.message || "Failed to send OTP. Please try again."
      );
    }
    setOtpLoading(false);
  };

  // Verify OTP and login
  const handleVerifyOtp = useCallback(async (e) => {
    if (e) e.preventDefault();
    const currentOtpString = otp.join("");
    if (currentOtpString.length !== 4) return;
    
    setOtpError("");
    setOtpLoading(true);
    try {
      const { data } = await axios.post(`/api/auth/login-otp`, { email, otp: currentOtpString });
      // LOGIN action in reducer will handle saving to cookie and localStorage
      dispatch({ type: "LOGIN", payload: data });
      
      navigate("/");
      // Auto-refresh the page after successful login
      window.location.reload();
    } catch (err) {
      // Clear OTP on error
      setOtp(["", "", "", ""]);
      // Focus back to first input
      if (otpRefs.current[0]) otpRefs.current[0].focus();
      setOtpError(
        err.response?.data?.message || "Invalid OTP. Please try again."
      );
    }
    setOtpLoading(false);
  }, [otp, email, dispatch, navigate]);

  const handleOtpChange = (e, idx) => {
    const value = e.target.value.replace(/\D/g, "");
    if (!value) return;
    const newOtp = [...otp];
    newOtp[idx] = value[0];
    setOtp(newOtp);
    if (idx < 3 && value) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otp[idx]) {
        const newOtp = [...otp];
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        otpRefs.current[idx - 1]?.focus();
      }
    }
  };

  const otpString = otp.join("");

  // Auto-verify OTP when all 4 digits are entered
  useEffect(() => {
    if (otpString.length === 4 && step === 1 && !otpLoading) {
      handleVerifyOtp();
    }
  }, [otpString, step, otpLoading, handleVerifyOtp]);

  const handleGoogleCredential = useCallback(async (response) => {
    try {
      setGoogleLoading(true);
      const token = response.credential;
      const { data } = await axios.post(`/api/auth/google`, { token });
      dispatch({ type: "LOGIN", payload: data });
      Cookies.set("user", JSON.stringify(data));
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.message || "Google login failed.");
    } finally {
      setGoogleLoading(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!googleScriptReady || !window.google?.accounts?.id) return;
    try {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      const div = document.getElementById("googleSignInDiv");
      if (div && div.childElementCount === 0) {
        window.google.accounts.id.renderButton(div, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
        });
      }
    } catch (e) {
      console.error("Google init error", e);
    }
  }, [handleGoogleCredential, googleScriptReady]);

  return (
    <div className="login_wrap">
      <div className="login_1">
        <img src="/images/socialNetwork.png" alt="UsmaApp Logo" />
        <span className="login_tagline">
          <b>
            {/* <i>
              <span className="login_tagline_line">UsmaApp - a calm space to connect, Share your moments in the most beautiful and effortless way.</span>
            </i> */}
          </b>
        </span>
      </div>

      <div className="login_2">
        <div className="login_2_wrap">
          {step === 0 && (
            <form>
              <input
                type="text"
                placeholder="Enter your email address"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="register_plain_input"
                style={{ width: 300, padding: 10, marginBottom: 10 }}
              />
              <button
                className="blue_btn"
                type="button"
                onClick={handleSendOtp}
                disabled={otpLoading || !email}
                style={{ marginTop: 16 }}
              >
                {otpLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <DotLoader color="#ffffff" size={16} />
                    <span>Sending OTP...</span>
                  </div>
                ) : (
                  "Send OTP"
                )}
              </button>
              {otpError && <div className="error_text">{otpError}</div>}
            </form>
          )}
          {step === 1 && (
            <form onSubmit={handleVerifyOtp}>
              <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 10 }}>
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(e, idx)}
                    onKeyDown={e => handleOtpKeyDown(e, idx)}
                    ref={el => otpRefs.current[idx] = el}
                    className="register_plain_input"
                    style={{ width: 50, height: 50, textAlign: "center", fontSize: 22 }}
                  />
                ))}
              </div>
              {otpError && <div className="error_text">{otpError}</div>}
              <button
                className="blue_btn"
                type="button"
                onClick={handleSendOtp}
                disabled={otpLoading}
                style={{ marginTop: 8 }}
              >
                {otpLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <DotLoader color="#ffffff" size={16} />
                    <span>Resending...</span>
                  </div>
                ) : (
                  "Resend OTP"
                )}
              </button>
            </form>
          )}
          <DotLoader color="#1876f2" loading={otpLoading || loading} size={30} />
          {error && <div className="error_text">{error}</div>}
          <div className="sign_splitter"></div>
          {/* Google Sign-In */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '12px', marginTop: 8 }}>
            <div id="googleSignInDiv" style={{ display: 'flex', justifyContent: 'center' }}></div>
            {!googleScriptReady && (
              <button type="button" className="blue_btn" disabled>
                {googleLoading ? null : "Loading Google Sign-In…"}
              </button>
            )}
            {googleLoading && <DotLoader color="#1876f2" loading={true} size={24} />}
          </div>
          <button
            className="blue_btn open_signup"
            onClick={() => setVisible(true)}
          >
            Create New Account
          </button>
        </div>

        {/* <Link to="/" className="sign_extra">
          <b>Create a Page</b> for a celebrity, brand or business.
        </Link> */}
      </div>
    </div>
  );
}
